import { motion } from "framer-motion";
import { NOOR } from "@/constants/testIds/noor";
import { fadeUp, fadeRight, fadeUpSmall, trans } from "./motion";
import { BOTTLE_BACK_TALL } from "./bottle";

const AGAVE = "https://images.unsplash.com/photo-1762715281787-beaeb988749f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWdhdmUlMjBwbGFudHxlbnwwfHx8fDE3ODE0NDk3NjN8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { n: "01", title: "Harvest", body: "Eight-year piñas selected by the jimadores at first light, in the highlands of Jalisco." },
  { n: "02", title: "Slow Roast", body: "Stone ovens, low fire, long hours — until the agave sings sweet caramel and earth." },
  { n: "03", title: "Twin Copper", body: "Twice distilled in hand-hammered copper alembics, never hurried, never filtered to silence." },
  { n: "04", title: "House of Crystal", body: "Bottled one at a time in jewelled crystal, sealed beneath a crown of rose-gold filigree." },
];

const AGAVE_BG = {
  backgroundImage: `url(${AGAVE})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  opacity: 0.12,
  mixBlendMode: "luminosity",
};

const AGAVE_OVERLAY = {
  background: "linear-gradient(180deg, var(--bg-surface) 0%, rgba(20,8,22,0.7) 50%, var(--bg-surface) 100%)",
};

function Step({ n, title, body, index }) {
  return (
    <motion.div
      {...fadeUpSmall}
      transition={trans(0.7, index * 0.1)}
      className="grid grid-cols-12 gap-4 items-start"
    >
      <p className="font-display col-span-2 text-3xl" style={{ color: "var(--accent-rose)" }}>{n}</p>
      <div className="col-span-10">
        <h3 className="font-display text-2xl mb-2" style={{ color: "var(--text-primary)" }}>{title}</h3>
        <p className="font-light text-sm md:text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{body}</p>
        <div className="hairline mt-5" />
      </div>
    </motion.div>
  );
}

function CraftIntro() {
  return (
    <div className="grid grid-cols-12 gap-y-10 md:gap-10 items-end mb-20">
      <div className="col-span-12 lg:col-span-7">
        <p className="overline mb-4">II · The Craft</p>
        <motion.h2
          {...fadeUp}
          transition={trans(1)}
          className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
          style={{ color: "var(--text-primary)" }}
        >
          Six bottles. <br />
          <em className="italic" style={{ color: "var(--accent-rose)" }}>One harvest.</em>{" "}
          Released only when the agave is ready.
        </motion.h2>
      </div>
      <div className="col-span-12 lg:col-span-5">
        <p className="text-base md:text-lg font-light leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Every batch is numbered, every bottle signed. We do not chase volume — we wait
          on the fields. When the agave is ready, six bottles emerge.
        </p>
      </div>
    </div>
  );
}

const ATELIER_PARAGRAPHS = [
  "The copper-rose filigree that wraps every NOOR vessel is not borrowed from any catalogue of ornament. We trained a private generative model on three centuries of Mughal jali screens, the silver-thread mantón shawls of Jalisco, and the agave's own crown of fronds. From that constellation the model surfaced thousands of motifs we had never seen. We chose one.",
  "Master metalworkers in Guadalajara then read the digital filigree the way a sculptor reads marble — translating each curl into hand-pulled rose-gold wire and the amethyst diadem that crowns the bottle. The vessel itself was cut from a parametric model that began with a single design constraint — light must enter the crystal and refract through the blanco as if dawn were striking still water.",
  "What you hold is the result of a quiet collaboration between centuries of human craft and a system that learned, briefly, to dream alongside it. A small batch is the only honest scale for work of this kind. We made one thousand bottles. We will make a thousand more, and then we will stop.",
];

function AtelierStory() {
  return (
    <div
      data-testid="craft-atelier-story"
      className="grid grid-cols-12 gap-y-10 md:gap-10 mb-24 md:mb-32 relative"
    >
      <div className="col-span-12 lg:col-span-3">
        <p className="overline mb-4" style={{ color: "var(--accent-rose)" }}>II.i · The Atelier</p>
        <p className="font-mono text-[10px] leading-relaxed" style={{ color: "var(--text-secondary)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
          Generative provenance<br />Hand-finished in Jalisco
        </p>
      </div>
      <div className="col-span-12 lg:col-span-9 lg:pl-8">
        <motion.h3
          {...fadeUp}
          transition={trans(0.9)}
          className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] mb-10 md:mb-12"
          style={{ color: "var(--text-primary)" }}
        >
          The filigree began in a{" "}
          <em className="italic" style={{ color: "var(--accent-rose)" }}>latent space.</em>
        </motion.h3>
        {ATELIER_PARAGRAPHS.map((p, i) => (
          <motion.p
            key={p.slice(0, 32)}
            {...fadeUpSmall}
            transition={trans(0.8, 0.2 + i * 0.12)}
            className="font-display-light text-lg md:text-xl leading-[1.65] mb-7 max-w-[60ch]"
            style={{ color: "var(--text-secondary)" }}
          >
            {p}
          </motion.p>
        ))}
        <motion.p
          {...fadeUpSmall}
          transition={trans(0.8, 0.6)}
          className="font-display italic text-base md:text-lg mt-10"
          style={{ color: "var(--accent-rose)" }}
        >
          Light, in Arabic, is <span className="not-italic">noor.</span> We named the spirit after what it does.
        </motion.p>
      </div>
    </div>
  );
}

function CraftBody() {
  return (
    <div className="grid grid-cols-12 gap-y-10 md:gap-10 items-center">
      <motion.div {...fadeRight} transition={trans(1.2)} className="col-span-12 lg:col-span-6">
        <img
          data-testid={NOOR.craftBoxImage}
          src={BOTTLE_BACK_TALL}
          alt="NOOR small-batch crystal vessel — detailed view"
          loading="lazy"
          decoding="async"
          className="w-full object-contain rounded-none"
          style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.6)" }}
        />
      </motion.div>
      <div className="col-span-12 lg:col-span-6 lg:pl-12">
        <div className="space-y-10">
          {STEPS.map((s, i) => <Step key={s.n} {...s} index={i} />)}
        </div>
      </div>
    </div>
  );
}

export default function Craft() {
  return (
    <section
      data-testid={NOOR.craftSection}
      id="craft"
      className="relative py-32 md:py-44 overflow-hidden"
      style={{ background: "var(--bg-surface)" }}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={AGAVE_BG} />
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={AGAVE_OVERLAY} />
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
        <CraftIntro />
        <AtelierStory />
        <CraftBody />
      </div>
    </section>
  );
}
